/**
 * MarkDepositReceivedModal - Modal for marking a deposit as paid manually
 * 
 * Gold-standard modal pattern following existing InvoicePaymentModal styling.
 * Used when contractor received payment outside the app (cash/check/etc).
 * 
 * IMPORTANT: This modal calls the SAME canonical endpoint that the online
 * deposit flow uses for setting deposit_paid fields. Invoice conversion
 * will pick up the deposit credit identically.
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { CheckCircle, SpinnerGap, CurrencyDollar, Info } from "phosphor-react";
import { formatCurrency } from "../../utils/formatters";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MarkDepositReceivedModal({
  isOpen,
  onOpenChange,
  depositInvoice,
  onSuccess,
}) {
  const [method, setMethod] = useState("check");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!depositInvoice) return;
    
    setSubmitting(true);
    try {
      let response;
      
      // Option 1: Use new estimate-level endpoint
      if (depositInvoice.isOption1 && depositInvoice.estimate_id) {
        response = await fetch(`${API}/estimates/${depositInvoice.estimate_id}/mark-deposit-received`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            method,
            note: note || null,
          }),
        });
      } else {
        // Option 2 (legacy): Use existing invoice endpoint
        response = await fetch(`${API}/invoices/${depositInvoice.invoice_id}/mark-deposit-paid`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            method,
            note: note || null,
          }),
        });
      }

      if (response.ok) {
        toast.success("Deposit marked as paid");
        onOpenChange(false);
        if (onSuccess) onSuccess();
      } else if (response.status === 409) {
        toast.error("Deposit is already marked as paid");
        onOpenChange(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to mark deposit as paid");
      }
    } catch (error) {
      console.error("Mark deposit paid error:", error);
      toast.error("Failed to mark deposit as paid");
    } finally {
      setSubmitting(false);
    }
  };

  if (!depositInvoice) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="mark-deposit-paid-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">
            Mark Deposit as Paid
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Use this if you received the deposit outside the app (cash, check, etc).
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-4">
            {/* Amount Display */}
            <div className="bg-emerald-50 p-4 rounded-sm text-center">
              <p className="text-sm text-emerald-600">Deposit Amount</p>
              <p className="font-heading text-3xl font-bold text-emerald-700">
                {formatCurrency(depositInvoice.total || depositInvoice.amount || 0)}
              </p>
            </div>

            {/* Payment Method */}
            <div>
              <Label>How was it paid?</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1 h-12 border-2" data-testid="deposit-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Credit/Debit Card (manual)</SelectItem>
                  <SelectItem value="transfer">Bank Transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Optional Note */}
            <div>
              <Label>Note (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Check number, transaction ID, or other details..."
                className="mt-1 border-2"
                rows={2}
              />
            </div>

            {/* Info box */}
            <div className="flex items-start gap-2 text-zinc-600 bg-zinc-50 p-3 rounded-sm">
              <Info size={16} className="mt-0.5 flex-shrink-0" />
              <span className="text-xs">
                This will be reflected when you convert to an invoice. The deposit amount will be credited automatically.
              </span>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="flex-1 border-2"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="confirm-deposit-paid-btn"
          >
            {submitting ? (
              <SpinnerGap size={18} className="mr-2 animate-spin" />
            ) : (
              <CheckCircle size={18} className="mr-2" />
            )}
            Mark Paid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
